import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ConfigBuilderService } from '@app/services/config-builder.service';
import { TitleService } from '@app/services/title.service';
import featureData from '@app/assets/page_feature.json';
import { RunnerData } from '@app/shared/components/argument-card/runner-details-dialog/runner-details';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { AuthService } from '@app/services/auth/auth.service';
import { Messages } from '@app/util/messages';
import { Messages as msg } from '@app/util/messages.util';
import { TourGuideContent } from '@app/util/tour-guide-content.util'
import { TourGuideService } from '@app/services/tour-guide.service';
import { ToastService } from '@app/services/toast.service';
import { ConstantsService } from '@app/services/constants.service';
import { MatSelect } from '@angular/material/select';
import { WfFeaturesService } from '@app/services/wf-features.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonFunctionalitiesService } from '@app/services/common-functionalities.service';

export interface ColFormat {
  id: string,
  title: string,
  type: string,
  value: any | RunnerData,
  options?: IDropdown[],
  specifiedType?: string,
}
export interface IDropdown {
  id: number;
  value: any;
  text: string;
  default: boolean;
}


export interface Argument {
  build_target: string;
  proj_repo_name: string;
  workitem_site: string;
  workitem_ticket: string;
}

export interface Config {
  arguments: any;
  brName: string;
  branchType: string;
  cng: any;
  cngName: string;
  status: string;
  repoUrl: string;
  rpId: number;
  rpName: string;
  wfId: number;
  wfName: string;
  wfhId: number;
  wfhName: string;
}

@Component({
  selector: 'app-table-expandable-editable',
  templateUrl: './table-expandable-editable.component.html',
  styleUrls: ['./table-expandable-editable.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})

export class TableExpandableEditableComponent implements OnChanges {

  /****************** Variable declaration ******************/
  getNoData = Messages.no_data;
  key: string = "wfId";
  expandedElement: Config | null | undefined;
  changedRow: any;
  id: number | undefined;
  mode: any;
  rowData = {};
  confirmDialogService: any;
  features: any = {};
  tourGuideIds = TourGuideContent;

  /**Manual wf trigger */
  dropdownVal: boolean | undefined;
  arrowUp: boolean = false;

  /**Edit config name */
  editableColumnIndex: any;
  editedValue: string;
  workflowForm: FormGroup;
  cngStatus: any;

  /* Hover titles */
  expandIcon = this.titleService.expandIcon;
  collapseIcon = this.titleService.collapseIcon;
  saveIcon = this.titleService.saveIcon;
  cancelIcon = this.titleService.cancelIcon;
  editIcon = this.titleService.editIcon;
  deleteIcon = this.titleService.deleteIcon;

  /****************** View child ******************/
  @ViewChildren(MatSelect) selectStatus: QueryList<MatSelect>;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /****************** Input and Output decorators ******************/
  @Input() dataSource: any;
  @Input('targetData') set targetData(data: any) {

    if (this.targetExec && data) {
      this.dataSource = data;
    }
  }
  @Input() columnsToDisplay!: ColFormat[];
  @Input() displayedColumns: any;
  @Input() columnsToDisplayWithExpand: any;
  @Input() expandable: boolean | undefined;
  @Input() editable: boolean | undefined;
  @Input() tourPage: any;
  /**Manual wf trigger */
  @Input() manualWf: any;
  @Input() targetExec: any;
  @Input() jobId: any;
  /**Edit config name */
  @Input() isEditActive: boolean = false;
  @Output() statusUpdated = new EventEmitter<{ id: number, active: any }>();

  /**
   * Constructor
   * @param configBuilderService 
   * @param titleService 
   * @param dialog 
   * @param tourGuideService 
   * @param toastService 
   * @param authService 
   * @param constantsService 
   * @param wfFeatureService 
   * @param fb 
   */
  constructor(private configBuilderService: ConfigBuilderService,
    private titleService: TitleService,
    public dialog: MatDialog,
    public tourGuideService: TourGuideService,
    private toastService: ToastService,
    public readonly authService: AuthService,
    public constantsService: ConstantsService,
    private wfFeatureService: WfFeaturesService,
    private fb: FormBuilder,
    public cf: CommonFunctionalitiesService) {
    this.parseFeatures();
    this.checkPermissions();
  }

  /**Edit configuration - Config name, config status */
  buildForm() {
    this.workflowForm = this.fb.group({
      cngName: ['', [Validators.required, Validators.maxLength(150)]],
    })
  }

  /**Function is used to set errors in update config name */
  getConfigurationName() {
    if (this.workflowForm.controls['cngName'].errors?.['required']) {
      // If 'cngName' is required, return the required message
      return msg.addConfigNew.cfgName.required;
    }
    else if (this.workflowForm.controls['cngName'].errors?.['maxlength']) {
      // If 'cngName' has exceeded its maximum length, return the maximum length message
      return msg.addConfigNew.cfgName.maxLength;
    }
    // If there are no errors, return an empty string
    return this.workflowForm.controls['cngName'].errors ? msg.addConfigNew.cfgName.invalid : '';
  }

  /**Function is used to set the config status value and send to workflow config component */
  statusValChange(value: boolean | null) {
    this.cngStatus = value
  }
  /**END: Edit configuration - Config name, config status */

  /**
   * This method parses the input JSON file and assigns boolean values to each feature mentioned in the file.
   * The features object can be used to keep track of which features are enabled or disabled in the application.
   */
  parseFeatures() {
    const config = JSON.parse(JSON.stringify(featureData)); // Parse the JSON file
    for (let i = 0; i < config.length; i++) {
      for (let j = 0; j < config[i].feature.length; j++) {
        const feature = config[i].feature[j];
        this.features[`${feature}`] = false; // Assign a boolean value to each feature
      }
    }
  }

  checkPermissions() {
    for (const obj in this.features) {
      if (this.authService.hasFeaturePermission(obj)) {
        this.features[obj] = true;
      }
    }
  }

  /**reload function is used to reload the table and get the latest details */
  reload() {
    this.wfFeatureService.getWorkflowName(true).subscribe((response: any) => {
      this.dataSource = new MatTableDataSource<any>(response?.data.dbResp);
      this.dataSource.paginator = this.paginator;
    });
  }

  /**
   * Expandable edit dialog box will expand if confirmed (yes)
   * @param element
   */
  editDialog(element: any) {
    this.confirmDialogService.confirmDialog({
      title: Messages.confirmdialog_edit_title,
      message: Messages.confirmdialog_edit_msg
    }).afterClosed().subscribe((dialogResult: boolean) => {
      if (dialogResult == true) {
        this.expandedElement = this.expandedElement === element ? null : element;
      }
    });
  }

  /**On click of edit in config builder expand element and if alredy expanded then close the element */
  toggleExpandElement(element: any, index: number): void {
    if (!this.authService.permissions['edit-config']) return; // check access
    if (this.expandedElement === element) {
      if (this.mode === 'edit') {
        this.expandedElement = null;
        this.mode = '';
        this.isEditActive = false;
      } else {
        this.mode = 'edit';
        this.setId(element, index);
      }
    } else {
      this.expandedElement = element;
      this.setId(element, index);
      this.mode = 'edit';
    }
    this.cngStatus = '' //reset the status on expand
  }

  /** ngOnChanges */
  ngOnChanges(): void {
    this.changedRow = JSON.parse(JSON.stringify(this.dataSource));
    this.rowData = this.dataSource[0];
    this.dataSource = new MatTableDataSource<any>(this.changedRow);
    this.dataSource.paginator = this.paginator;

    // If target build execution - toggle run workflow button
    this.targetExec ? this.toggleElement(this.dataSource.data[0], 0) : '';
  }

  /** onChange */
  onChange(i: any, field: string, event: any) {
    let obj = this.changedRow[i];
    if (event.hasOwnProperty('target')) {
      obj[field] = event.target.value;
    } else {
      obj[field] = event.value
    }
    this.changedRow[i] = obj;
  }

  /**
   * Deletes the saved configuration
   * @param i
   */
  onDelete(i: number) {
    if (this.tourGuideService.isStartTour) {
      this.toastService.showSuccess(
        '',
        'Workflow configuration deleted !!'
      )
      return;
    }
    if (!this.authService.permissions['delete-config']) return; //check access

    const dialogData = new ConfirmDialogModel(Messages.confirmdialog_delete_title, Messages.confirmdialog_delete_msg);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "370px",
      disableClose: true,
      data: dialogData
    });
    dialogRef.afterClosed().subscribe((dialogResult: boolean) => {

      if (dialogResult === true) {
        // Calculate the correct index based on current page and page size
        const currentPage = this.paginator ? this.paginator.pageIndex : 0;
        const pageSize = this.paginator ? this.paginator.pageSize : 10;
        const actualIndex = (currentPage * pageSize) + i;

        let wfEntities = JSON.parse(JSON.stringify(this.dataSource?.filteredData[actualIndex]));
        let id = wfEntities.value;
        let wfName = wfEntities.name;
        this.configBuilderService.deleteWorkflowConfig(id, wfName).then(() => {
          this.reload();
        })
      }
    });
  }

  /**
   * Saves the configuration
   * @param i
   */
  onSave(i: number) {
    this.dataSource = this.changedRow;
    this.id = undefined;

  }

  /**
   * Cancels the configuration while updating the fields
   * @param i
   */
  onCancel(i: number) {
    this.changedRow = this.dataSource
    this.id = undefined;

  }

  /**setId is used to set the row id to this.id */
  setId(row: any, index: number) {
    this.id = index;
    this.buildForm();
    this.isEditActive = true;
    this.editableColumnIndex = row;
    this.editedValue = this.dataSource.data.find(((el: any) => el.value === row.value))?.name;
    this.workflowForm.get('configNm')?.setValue(this.editedValue)
  }

  /** is edit active or not */
  isEdit(event: any) {
    this.isEditActive = event
  }

  /** Add icon click functionality */
  expand(row: any, index: number) {
    this.id = index;
    this.buildForm();
    this.editableColumnIndex = row;
    this.editedValue = this.dataSource.data.find(((el: any) => el.value === row.value))?.name;
    this.workflowForm.get('configNm')?.setValue(this.editedValue)
    this.cngStatus = '' //reset the status on expand
  }

  /** On click on minimize icon */
  remove() {
    this.isEditActive = false;
  }

  /** updateExpand is used to update the element on epand */
  updateExpand($event: any) {
    this.expandedElement = $event
  }

  /**
   * Manual wf trigger status change execution (active/inactive)
   * @param event 
   */
  onStatusChange(event: any, rowData: any, i: number) {
    this.dropdownVal = JSON.parse(event.value);

    const dialogData = new ConfirmDialogModel(Messages.confirmdialog_edit_title, Messages.confirmdialog_edit_msg);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "370px",
      disableClose: true,
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((dialogResult: boolean) => {
      if (dialogResult === true) {
        this.expandedElement = null;
        this.arrowUp = false;

        let json = {
          'active': this.dropdownVal
        }

        this.configBuilderService.updateActiveFlg(rowData.id, json).subscribe((resp: any) => {
          this.toastService.showSuccess(msg.success, msg.manualWfTrigger.wfStatusChangeSuccess);
          rowData.active = this.dropdownVal;
          this.statusUpdated.emit(rowData);
        }, (errors: any) => {
          this.toastService.showError(msg.failed, msg.manualWfTrigger.wfStatusChangeFail);
          this.setStatus(rowData, i)
        });
      } else {
        this.setStatus(rowData, i)
      }
    });
  }

  /**If status api fail display the exisiting value, and if dialog result 'No' selected show exisiting value */
  setStatus(rowData: any, i: any) {
    this.selectStatus.forEach((selectStatus: MatSelect, index: number) => {
      if (index == i && selectStatus) {
        selectStatus.writeValue((rowData.active).toString());
      }
    })
  }

  /** table change based on page */
  tableIdChangeBasedOnPage() {
    if (this.tourPage === 'workflow-config') {
      return this.tourGuideIds.configBuilder.workflow.configTable.id;
    } else if (this.tourPage === 'github-actions') {
      return this.tourGuideIds.githubAction.table.id;
    }
    return;
  }

  /**
   * toggleElement is used to change the up arrow and down arrow based on the expanded element
   * @param element 
   */
  toggleElement(element: any, i: any) {
    this.expand(element, i);
    this.expandedElement = this.expandedElement === element ? null : element;

    if (this.expandedElement === element) {
      this.arrowUp = true;
    } else {
      this.arrowUp = false;
    }
  }
}
